import React, { useState, useCallback, useEffect } from 'react';
import { TextInput as RNTextInput, Platform } from 'react-native';

import { trim } from 'lodash';
import { formatError } from '@nakedplatform/support';
import { NakedIcon } from 'theme/icons';
import { Colors } from 'theme/colors';
import { Container } from 'theme/layout';
import { useViewType, ViewType } from 'hooks/screen';

import { ClearanceCaret } from './clearance';
import { ErrorMessage } from './error';
import { InputIcon } from './icon';
import { InputContainer, TextInput, SecureSwitch, TextInputProps } from './styles';

const MOBILE_ICON_SIZE = 19;
const TABLET_ICON_SIZE = 24;
const ICON_COLOR = Colors.black;
const FontSizeMap = {
  [ViewType.MOBILE]: '16px',
  [ViewType.DESKTOP]: '17px',
  [ViewType.TABLET]: '17px',
};

/**
 * Retrieves style props
 *
 * @param input - Style input props
 */
function getStyleProps(input: StyleInputProps) {
  const { clearable, icon, secureTextEntry } = input;
  const doubleIcon = secureTextEntry && clearable;
  const padRight = doubleIcon ? '70px' : secureTextEntry || clearable ? '40px' : '20px';
  const padLeft = icon ? '40px' : '20px';
  const paddingRight = `${padRight}!important`;
  const paddingLeft = `${padLeft}!important`;

  return {
    paddingRight,
    paddingLeft,
  };
}
/**
 * Input field
 *
 * @param props - Input props
 * @param ref - React Ref
 */
function InputFieldComponent(
  props: Props,
  ref: React.MutableRefObject<RNTextInput & HTMLInputElement>
) {
  const {
    editable,
    clearable,
    icon,
    secureTextEntry,
    iconSize,
    iconColor,
    onChangeText,
    borderColor,
    backgroundColor,
    onFocus,
    onBlur,
    placeholder,
    error,
    touched,
    focused,
    name,
    shouldValidateInput,
    errorPosition,
    accentColor,
    ...otherProps
  } = props;
  const [currentText, setCurrent] = useState(props.value || '');
  const [errorMessage, setErrorMessage] = useState('');
  const [visibility, setVisibility] = useState(secureTextEntry);
  const { paddingLeft, paddingRight } = getStyleProps({
    clearable,
    icon,
    secureTextEntry,
  });
  const viewType = useViewType();
  const fontSize = FontSizeMap[viewType];

  const switchVisibility = useCallback(() => {
    setVisibility(visible => !visible);
  }, []);

  const clearEntry = useCallback(() => {
    ref.current.clear();
    onChangeText('');
    setCurrent('');
  }, [ref, onChangeText]);

  const handleOnChange = useCallback((text: string) => {
    setCurrent(text);
  }, []);

  const handleOnBlur = useCallback(
    e => {
      if (onBlur) {
        onBlur(e);
      }

      setCurrent(trim(currentText));
    },
    [onBlur, currentText]
  );

  useEffect(() => {
    if (touched && shouldValidateInput) {
      setErrorMessage(formatError(error, placeholder, name));
    }
  }, [touched, shouldValidateInput, error, placeholder, name]);

  useEffect(() => {
    if (onChangeText) {
      onChangeText(currentText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentText]);

  return (
    <Container
      position="relative"
      style={{
        transition: 'margin-top 0.3s ease-in-out',
      }}
    >
      <ErrorMessage message={errorMessage} errorPosition={errorPosition} />
      <InputContainer
        backgroundColor={backgroundColor}
        editable={editable}
        focused={focused}
        errorMessage={errorMessage}
        borderColor={borderColor}
        accentColor={accentColor}
      >
        <InputIcon
          name={icon}
          size={iconSize || switchIconSize(viewType)}
          color={switchIconColor(iconColor)}
        />
        <TextInput
          ref={ref}
          secureTextEntry={visibility}
          paddingRight={paddingRight}
          paddingLeft={paddingLeft}
          editable={editable}
          fontSize={fontSize}
          focused={focused}
          onFocus={onFocus}
          onBlur={handleOnBlur}
          onChangeText={handleOnChange}
          errorMessage={errorMessage}
          placeholder={placeholder}
          height={['45px', '48px']}
          {...otherProps}
        />
        <ClearanceCaret
          onPress={clearEntry}
          isClearable={clearable}
          isSecure={secureTextEntry}
          size={clearanceIconSize(iconSize, switchIconSize(viewType))}
          color={switchIconColor(iconColor)}
        />
        {secureTextEntry && (
          <SecureSwitch onPress={switchVisibility}>
            <NakedIcon name={switchIcon(visibility)} size={MOBILE_ICON_SIZE} color={Colors.black} />
          </SecureSwitch>
        )}
      </InputContainer>
    </Container>
  );
}
/**
 * Get the switch icon
 *
 * @param visibility - whether it should be visible or not
 */
function switchIcon(visibility: boolean) {
  return visibility ? 'eye-close' : 'eye-open';
}
/**
 * Get the correct switch icon size
 *
 * @param viewType - view type
 */
function switchIconSize(viewType: ViewType) {
  return viewType !== ViewType.MOBILE ? TABLET_ICON_SIZE : MOBILE_ICON_SIZE;
}
/**
 * This function toggles the visibility icon of secure text fields
 *
 * @param color - switch icon color
 */
function switchIconColor(color: Colors): Colors {
  return color ? color : ICON_COLOR;
}
/**
 * Returns the correct icon size for the clearance button
 *
 * @param size  - Preferred icon size
 * @param defaultSize - Default icon size
 */
function clearanceIconSize(size?: number, defaultSize?: number) {
  return size || defaultSize;
}
export const InputField = React.forwardRef(InputFieldComponent);

interface StyleInputProps {
  /** The icon to use with the field */
  icon?: string;
  /** Indicates whether the input is password or not */
  secureTextEntry?: boolean;
  /** Whether the field implements a clearing button*/
  clearable?: boolean;
}

interface Props extends TextInputProps {
  /** The icon to use with the field */
  icon?: string;
  /** The size of the icon as font size */
  iconSize?: number;
  /** The color of the icon */
  iconColor?: Colors;
  /** The background color for the input */
  backgroundColor?: string;
  /** Whether the field implements a clearing button*/
  clearable?: boolean;
  /** The name of the field */
  name?: string;
  /** Whether the field is touched */
  touched?: boolean;
  /** Whether to validate the filed input */
  shouldValidateInput?: boolean;
  /** Position the error */
  errorPosition?: string;
  /** Color with which to override the accent color */
  accentColor?: string;
}
